import {
  Activity,
  BookOpen,
  Briefcase,
  ChevronDown,
  CircleHelp,
  FilePlus2,
  FlagTriangleRight,
  GitBranch,
  Icon,
  type IconComponent,
  Inbox,
  KeyRound,
  ListTodo,
  Network,
  ScrollText,
  ShieldAlert,
  Table2,
  UserMinus,
  UserPen,
  Users,
} from "@norican/icons";
import { Button, Menu } from "@norican/react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";

import { NoricanLogo } from "@/components/norican-logo";
import { control } from "@/components/table";
import { useLogout, useMe, type UserProfile } from "@/lib/api";
import { maySeeOperations } from "@/lib/capability";
import { type ThemeChoice, useThemeChoice } from "@/lib/theme";

/**
 * The only chrome the app has: product name, four menus, two links, sign out.
 *
 * It started as five text links. By 6 Oct 2026 there were eight, three of
 * which open pages with tabs of their own, and Timo asked for "submenus and
 * icons". So the pages are grouped by the job they belong to, each group is a
 * dropdown whose items deep-link straight to the tab, and every entry carries
 * an icon so the eye can find it without reading.
 *
 * Still no sidebar: HROps is a dense working tool used a few times a week,
 * and a permanent rail would spend a sixth of the screen on eight words.
 *
 * Visibility is decided here exactly as before: a group is hidden rather than
 * shown-and-refused when the person cannot use anything in it. The routes
 * remain the control; the nav only avoids teaching people to distrust it.
 */

type Item = {
  to: string;
  label: string;
  icon: IconComponent;
  /** One line under the label, for the entries whose name alone is not enough. */
  hint?: string;
  show?: (me: UserProfile | undefined) => boolean;
};

type Group = {
  key: string;
  label: string;
  icon: IconComponent;
  /** Which paths light this group up. */
  match: (pathname: string) => boolean;
  items: Item[];
  show?: (me: UserProfile | undefined) => boolean;
};

const GROUPS: Group[] = [
  {
    key: "leavers",
    label: "Leavers",
    icon: UserMinus,
    match: (p) => p.startsWith("/queue") || p.startsWith("/leaver"),
    items: [
      { to: "/queue", label: "Queue", icon: ListTodo, hint: "Open and finished offboardings" },
      {
        to: "/leaver",
        label: "New offboarding",
        icon: UserMinus,
        hint: "Name the leaver, the time and who takes the mailbox",
      },
    ],
  },
  {
    key: "risk",
    label: "Risk & access",
    icon: ShieldAlert,
    match: (p) => p.startsWith("/flags") || p.startsWith("/access"),
    items: [
      {
        to: "/flags",
        label: "Risk flags",
        icon: FlagTriangleRight,
        hint: "Time-boxed; high restricts",
      },
      {
        to: "/access",
        label: "Access lists",
        icon: KeyRound,
        hint: "Groups HR keeps up to date",
        show: (me) => me?.access_lists_enabled === true,
      },
    ],
  },
  {
    key: "org",
    label: "Organisation",
    icon: Network,
    match: (p) => p.startsWith("/org"),
    show: (me) => me?.org_chart_enabled === true,
    items: [
      { to: "/org?view=directory", label: "Directory", icon: Users, hint: "Reporting lines today" },
      {
        to: "/org?view=ad",
        label: "Active Directory",
        icon: Table2,
        hint: "Every person's work fields, read-only",
      },
      {
        to: "/org?view=scenarios",
        label: "Scenarios",
        icon: GitBranch,
        hint: "A target structure: prepare, approve, apply",
      },
      {
        to: "/org?view=adhoc",
        label: "Change an account",
        icon: UserPen,
        hint: "One person, written to AD on confirmation",
        // Off for everyone since 6 Oct 2026: the Workforce page carries this
        // change with an approval. The item comes back if the deployment
        // reopens the door (HROPS_ADHOC_UPNS).
        show: (me) => me?.adhoc_enabled === true,
      },
    ],
  },
  {
    key: "workforce",
    label: "Workforce",
    icon: Briefcase,
    match: (p) => p.startsWith("/workforce"),
    show: (me) => me?.workforce_enabled === true,
    items: [
      {
        to: "/workforce?view=report",
        label: "Report a change",
        icon: FilePlus2,
        hint: "Hire, exit, planned exit, future position, org change",
      },
      {
        to: "/workforce?view=requests",
        label: "Requests",
        icon: Inbox,
        hint: "Everything submitted, awaiting approval first",
      },
      {
        to: "/workforce?view=log",
        label: "Log",
        icon: ScrollText,
        hint: "Who did what, when, and what HROps did with it",
      },
      {
        to: "/help/workforce",
        label: "How it works",
        icon: BookOpen,
        hint: "The forms, the approval, the data, who sees what",
      },
    ],
  },
];

const TRIGGER_BASE =
  "flex items-center gap-1.5 rounded px-2 py-1 text-sm outline-none transition-colors " +
  "focus-visible:ring-border focus-visible:ring-2";
const TRIGGER_ACTIVE = "text-fg bg-bg-subtle font-medium";
const TRIGGER_IDLE = "text-fg-muted hover:text-fg hover:bg-bg-subtle";

export function AppNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const logoutMutation = useLogout();
  const me = useMe().data;

  const groups = GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.show || i.show(me)),
  })).filter((g) => (!g.show || g.show(me)) && g.items.length > 0);

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    <header className="border-border flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-4 py-2">
      <Link to="/queue" className="flex items-center gap-3 pr-2">
        <NoricanLogo height={18} />
        <span className="border-border h-4 border-l" aria-hidden="true" />
        <span className="font-display text-fg text-sm font-bold">HROps</span>
      </Link>
      <nav aria-label="Main" className="flex flex-wrap items-center gap-1">
        {groups.map((group) => (
          <GroupMenu key={group.key} group={group} active={group.match(location.pathname)} />
        ))}
        <PlainLink to="/help" label="Help" icon={CircleHelp} />
        {maySeeOperations(me) ? (
          // Last, and only for the handful of people who run this: it is the
          // technical view, not part of the HR job.
          <PlainLink to="/admin" label="Operations" icon={Activity} />
        ) : null}
      </nav>
      <div className="ml-auto flex items-center gap-3">
        <ThemeToggle />
        <Button variant="outline" size="sm" onClick={handleLogout}>
          Sign out
        </Button>
      </div>
    </header>
  );
}

function GroupMenu({ group, active }: { group: Group; active: boolean }) {
  return (
    <Menu.Root>
      <Menu.Trigger
        className={`${TRIGGER_BASE} ${active ? TRIGGER_ACTIVE : TRIGGER_IDLE}`}
        aria-current={active ? "page" : undefined}
      >
        <Icon icon={group.icon} size="sm" />
        {group.label}
        <Icon icon={ChevronDown} size="xs" className="opacity-60" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="start">
          <Menu.Popup width="content" className="min-w-64">
            {group.items.map((item) => (
              <Menu.Item key={item.to} render={<Link to={item.to} />}>
                <span className="flex items-start gap-2.5">
                  <Icon icon={item.icon} size="sm" className="mt-0.5 shrink-0" />
                  <span className="flex flex-col">
                    <span>{item.label}</span>
                    {item.hint ? (
                      <span className="text-fg-muted text-xs font-normal">{item.hint}</span>
                    ) : null}
                  </span>
                </span>
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

function PlainLink({ to, label, icon }: { to: string; label: string; icon: IconComponent }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `${TRIGGER_BASE} ${isActive ? TRIGGER_ACTIVE : TRIGGER_IDLE}`}
    >
      <Icon icon={icon} size="sm" />
      {label}
    </NavLink>
  );
}

/**
 * Light, dark, or whatever the machine says.
 *
 * A three-option select rather than a two-state sun/moon button, because
 * "system" is the honest default and a two-state toggle cannot express it: the
 * first click on such a toggle silently opts the user out of their own OS
 * setting, and there is no way back to it.
 */
function ThemeToggle() {
  const { choice, setChoice } = useThemeChoice();
  return (
    <label className="text-fg-muted flex items-center gap-2 text-xs">
      <span className="sr-only">Appearance</span>
      <select
        value={choice}
        onChange={(event) => setChoice(event.target.value as ThemeChoice)}
        className={control}
        aria-label="Appearance"
      >
        <option value="system">System</option>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
    </label>
  );
}
